/**
 * usePatchVariant — `PATCH /v1/off-page-briefs/:opbId/variants/:variantId`.
 *
 * Persists inline edits (body / title / target / hook selection) for a single
 * variant. The API clears the critique score + flips status back to `draft`
 * on any body change (spec §5.6.1), so we invalidate the detail cache to
 * pick up the fresh status + score without a manual refetch.
 *
 * The mutation preserves the raw `ApiError` so callers can pattern-match on
 * `error.code` (e.g. `VALIDATION_FAILED` for empty bodies) — per the same
 * convention as sibling hooks in `features/briefs/`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OffPageBriefVariant, PatchVariantRequest } from '@seo/types';
import { apiPatch } from '@/lib/api';

export interface UsePatchVariantArgs {
  variantId: string;
  body: PatchVariantRequest;
}

export function usePatchVariant(opbId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ variantId, body }: UsePatchVariantArgs) =>
      apiPatch<OffPageBriefVariant>(
        `/v1/off-page-briefs/${opbId}/variants/${variantId}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['off-page-briefs', 'detail', opbId],
      });
    },
  });
}
